import type { CSSProperties } from 'react';
import artIndex from '../../../assets/items/index.json';
import wornIndex from '../../../assets/items/worn/index.json';
import type { CatalogItem } from '../models/types';
import { catalog } from '../state/catalog';

const BACK_SUFFIX = '.back';

/** `assets/items/index.json`, written by tools/image-gen: an id per front image, `<id>.back` per back layer. */
const stems = artIndex as string[];
const withArt: ReadonlySet<string> = new Set(stems.filter((s) => !s.endsWith(BACK_SUFFIX)));
const withBack: ReadonlySet<string> = new Set(stems.filter((s) => s.endsWith(BACK_SUFFIX)).map((s) => s.slice(0, -BACK_SUFFIX.length)));

/** A box on the avatar's square canvas: left, top, right, bottom as fractions. */
export type AvatarBox = readonly [number, number, number, number];

/** An item as worn on the avatar: its layer image, drawn on the base body, and where it sits on the canvas. */
export interface WornLayer {
  url: string;
  box: AvatarBox;
}

/** `assets/items/worn/index.json`, written one item at a time by tools/image-gen's `wardrobe sample`. */
const worn = wornIndex as unknown as Record<string, AvatarBox>;

/** The item's worn layer, or `null` when it has none yet (it is then not drawn on the avatar). */
export function resolveWornLayer(itemId: string): WornLayer | null {
  const box = worn[itemId];
  return box && catalog.contains(itemId) ? { url: `/items/worn/${encodeURIComponent(itemId)}.webp`, box } : null;
}

/** How one item is drawn. The only place that decides between an image and the catalog emoji. */
export type ItemArtRef = { kind: 'image'; url: string; backUrl?: string } | { kind: 'emoji'; emoji: string };

/** The image when the item has one, otherwise its emoji; `null` for an id not in the bundled catalog. */
export function resolveItemArt(itemId: string): ItemArtRef | null {
  if (!catalog.contains(itemId)) return null;
  if (withArt.has(itemId)) {
    const url = `/items/${encodeURIComponent(itemId)}.webp`;
    return withBack.has(itemId) ? { kind: 'image', url, backUrl: `/items/${encodeURIComponent(itemId)}${BACK_SUFFIX}.webp` } : { kind: 'image', url };
  }
  return { kind: 'emoji', emoji: catalog.get(itemId).emoji };
}

/**
 * An item's art at the size of the surrounding text: an image is 1em square, so callers size it
 * with `font-size` exactly as they size an emoji.
 */
export function ItemArt({ item, className, style }: { item: CatalogItem; className?: string; style?: CSSProperties }) {
  const art = resolveItemArt(item.id);
  return (
    <span className={className} style={style} aria-hidden="true" data-art={art?.kind ?? 'emoji'}>
      {art?.kind === 'image' ? (
        art.backUrl ? (
          // Away from the avatar the whole item is shown: the back layer first, the front layer over it.
          <span className="item-art-stack">
            <img className="item-art" src={art.backUrl} alt="" loading="lazy" decoding="async" />
            <img className="item-art" src={art.url} alt="" loading="lazy" decoding="async" />
          </span>
        ) : (
          <img className="item-art" src={art.url} alt="" loading="lazy" decoding="async" />
        )
      ) : (
        item.emoji
      )}
    </span>
  );
}
